import { createSelector } from "reselect";

import { getFilteredLeadFinderRecords } from "@app/component/leadFinder/store/selectors";
import MapMode from "@app/enum/map/MapMode";
import Path from "@app/enum/Path";
import { getUserMetaData } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { leadIdGetter } from "@app/util/map/idGetters";

import { getGroupModeBasePath } from "./groupMode/selectors";
import { getSelectedRecords } from "./selectors";
import { getTerritoryModeBasePath } from "./territoryMode/selectors";

const mapState = (state: RootState) => state.scene.map;

// basic state
export const isInitializing = createSelector(mapState, ({ initializing }) => initializing);
export const getMapViewport = createSelector(mapState, ({ viewport }) => viewport);

export const getMapMode = createSelector(mapState, ({ mode }) => mode);
export const getMapModeBasePath = createSelector(
  mapState,
  getGroupModeBasePath,
  getTerritoryModeBasePath,
  ({ mode }, groupModeBasePath, territoryModeBasePath) => {
    switch (mode) {
      case MapMode.GROUPS:
        return groupModeBasePath;

      case MapMode.LEAD_FINDER:
        return `${Path.MAP}/leads`;

      case MapMode.TERRITORIES:
        return territoryModeBasePath;
    }

    return `${Path.MAP}`;
  }
);

// pin drop
export const getMapPinDrop = createSelector(mapState, ({ pinDrop }) => pinDrop);

// lasso
export const getMapLasso = createSelector(mapState, ({ lasso }) => lasso);
export const getMapLassoAllowAdd = createSelector(getMapLasso, (lasso) => !!lasso?.allowAdd);
export const getMapLassoPaths = createSelector(getMapLasso, (lasso) =>
  lasso?.paths && Array.isArray(lasso.paths) ? lasso.paths : []
);
export const getMapLassoPathsCount = createSelector(getMapLassoPaths, (paths) => paths.length);
export const isLassoLoading = createSelector(getMapLasso, ({ loading }) => loading);
export const isLassoContainsClusters = createSelector(
  getMapLasso,
  ({ containsClusters }) => containsClusters
);

export const getLassoOptions = createSelector(getMapLasso, ({ options }) => options);
export const getLassoMode = createSelector(getLassoOptions, (options) => options.mode);
export const getVisibleLassoEntityTypes = createSelector(
  getLassoOptions,
  (options) => options.visibleEntityTypes
);

// metaData settings

export const getUserLocationsSettings = createSelector(
  getUserMetaData,
  (metaData) => metaData.userLocationsSettings
);

export const getFrequencyActivityAction = createSelector(
  getUserMetaData,
  (metaData) => metaData.lastFrequencyActivityAction
);

export const getFrequencyCustomInterval = createSelector(
  getUserMetaData,
  (metaData) => metaData.frequencyCustomInterval
);

export const getFilteredSelectedRecords = createSelector(
  getFilteredLeadFinderRecords,
  getSelectedRecords,
  (records, selection) => records.filter((lead) => selection.has(leadIdGetter(lead)))
);

export const getFilteredSelectedRecordsCount = createSelector(
  getFilteredSelectedRecords,
  (records) => records.length
);

export const isLocationSearchVisible = createSelector(
  mapState,
  ({ locationSearchVisible }) => locationSearchVisible
);

export const getRulerPath = createSelector(mapState, ({ ruler }) => ruler.path);

// other selectors
export * from "./groupMode/selectors";
export * from "./mapMode/selectors";
export * from "./pinLocation/selectors";
export * from "./territoryMode/selectors";
export * from "./toolbar/selectors";
